import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_PACK_ANALYSIS_CARTON_TYPES_LIST,
  API_PACK_ANALYSIS_CARTON_TYPES_CREATE,
  API_PACK_ANALYSIS_CARTON_TYPES_UPDATE,
  API_PACK_ANALYSIS_CARTON_TYPES_DELETE,
  API_PACK_ANALYSIS_CARTON_TYPES_REACTIVATE,
  API_PACK_ANALYSIS_CARTON_TYPES_REFERENCES,
  API_PACK_ANALYSIS_CARTON_TYPES_SELF_SERVE_OVERRIDE,
  API_PACK_ANALYSIS_CARTON_TYPES_REMOVE_SCOPE,
} from '../../constants/apiConstants';

// ============================================================================
// Types — mirror SmartMatrix.Domain.PackAnalysis.Messages.CartonType_*.
// Spec: .kiro/specs/pack-analysis-carton-type-catalog-admin/design.md §Data Models
// ============================================================================

/** Full read shape of a carton type row, flattened from the BE entity. */
export interface CartonType {
  id: number;
  guid: string;
  fingerprint: string;
  description: string | null;
  category: string | null;

  length: number;
  width: number;
  height: number;
  dimensionUnit: string;

  weightCapacity: number | null;
  /** Legacy field — populated identically to weightUnit; prefer weightUnit. */
  weightCapacityUnit: string | null;
  weightUnit: string | null;
  tareWeight: number | null;

  volume: number | null;
  volumeUnit: string | null;

  isActive: boolean;
  createdAt: string | null;
  createdBy: string | null;
  modifiedAt: string | null;
  modifiedBy: string | null;

  // edi-master-data-scoped-self-serve: populated only in scoped-projection mode
  // (per-pair rendering). null in legacy tenant-wide mode. description/category
  // above carry the coalesced (overlay ?? canonical) values in projection mode.
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  // edi-master-data-scoped-self-serve (manage affordance): true when the caller
  // holds carton-type MANAGE for this row's pair (resolved per-party on the BE).
  // Drives the per-row self-serve override affordance — the party-blind login
  // permission list can't surface per-party EDI grants. undefined in legacy mode.
  canManage?: boolean;
  // edi-carton-type-scoped-physical-attributes: true when the canonical carton is
  // a platform-default shared seed (tenant-level fallback, no per-pair alias).
  // Seeds must NOT be deleted by a buyer/supplier (affects everyone) — the FE
  // shows the per-scope delete only when this is false.
  isPlatformDefault?: boolean;
}

export interface CategoryFacet {
  /** `null` means "rows with no category" (the special `(no category)` chip). */
  value: string | null;
  count: number;
}

export interface CartonTypeListResponse {
  items: CartonType[];
  /** `null` when the caller did not opt into paging (legacy list path). */
  total: number | null;
  page: number | null;
  pageSize: number | null;
  availableCategories: CategoryFacet[];
}

export interface CartonTypeListParams {
  tenantId: number;
  q?: string | null;
  /** Multi-value category filter. Empty string in the array selects rows with NULL category. */
  category?: string[] | null;
  includeInactive?: boolean;
  page?: number | null;
  pageSize?: number | null;
  // edi-master-data-scoped-self-serve: when set, the BE returns the per-pair
  // scoped projection (legacy tenant-wide when both omitted). A null on one axis
  // = "all" on that axis (e.g. buyerPartyId set + supplierPartyId omitted).
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
}

/** edi-master-data-scoped-self-serve — per-pair self-serve display override.
 * edi-carton-type-scoped-physical-attributes adds the per-(buyer,supplier) tare
 * override (carton types share one canonical row by dims fingerprint, but
 * suppliers' same-dim boxes weigh differently). Full-state PUT: send every field
 * you want to keep — a null clears that field; all-null clears the override row. */
export interface CartonTypeSelfServeOverrideRequest {
  buyerPartyId: number;
  supplierPartyId: number;
  descriptionOverride?: string | null;
  categoryOverride?: string | null;
  tareWeight?: number | null;
  weightUnit?: string | null;
  modifiedBy?: string | null;
}

export interface CartonTypeCreateRequest {
  /**
   * Fingerprint is auto-computed by the BE from normalized dims + weight cap
   * (matches the ASN ingest canonicalizer hash). Operators never enter it;
   * the field is retained on the DTO for backward compatibility with older
   * callers that still send it. New callers SHOULD omit it.
   */
  fingerprint?: string | null;
  description: string;
  category: string | null;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string;
  weightCapacity: number | null;
  weightUnit: string | null;
  tareWeight: number | null;
  volume: number;
  volumeUnit: string;
  createdBy?: string | null;
  // edi-carton-type-scoped-physical-attributes — adopt-or-create. When an active
  // (buyer, supplier) scope is supplied, the BE reuses the canonical on a
  // fingerprint hit (no duplicate error) and attaches a per-pair overlay
  // (description/category + tare) instead of rejecting.
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
}

export type CartonTypeUpdateRequest = Omit<CartonTypeCreateRequest, 'fingerprint' | 'createdBy' | 'buyerPartyId' | 'supplierPartyId'> & {
  modifiedBy?: string | null;
};

export interface CartonTypeReferenceCountResponse {
  cartonTypeId: number;
  activeApprovedListCount: number;
  activeProfileIds: number[];
}

/** Thrown when the BE returns `Fingerprint_Duplicate` (-2700) on create. */
export class CartonTypeFingerprintDuplicateError extends Error {
  readonly reasonCode = 'CARTON_TYPE_FINGERPRINT_DUPLICATE';
  constructor(message?: string) {
    super(message ?? 'A carton type with this fingerprint already exists.');
    this.name = 'CartonTypeFingerprintDuplicateError';
  }
}

/**
 * Thrown when the BE returns `Has_Active_References` (-2721) on deactivate.
 * The `activeReferenceCount` field surfaces the count so the FE can render
 * `"Cannot deactivate — used in {N} active list(s)"` (Spec A Req 4.2).
 */
export class CartonTypeHasActiveReferencesError extends Error {
  readonly reasonCode = 'CARTON_TYPE_HAS_ACTIVE_REFERENCES';
  readonly activeReferenceCount: number;
  constructor(activeReferenceCount: number, message?: string) {
    super(message ?? `Cannot deactivate — carton type is referenced by ${activeReferenceCount} active list(s).`);
    this.name = 'CartonTypeHasActiveReferencesError';
    this.activeReferenceCount = activeReferenceCount;
  }
}

/**
 * Thrown when the BE returns `Already_In_Scope` (-2702) on a scoped create —
 * the carton is already in this (buyer, supplier) pair's catalog (a prior
 * adoption or an ASN code alias). edi-carton-type-scoped-physical-attributes.
 */
export class CartonTypeAlreadyInScopeError extends Error {
  readonly reasonCode = 'CARTON_TYPE_ALREADY_IN_SCOPE';
  constructor(message?: string) {
    super(message ?? 'This carton type is already in your catalog for this buyer/supplier.');
    this.name = 'CartonTypeAlreadyInScopeError';
  }
}

/** Thrown when the BE returns `Not_Found` on update / deactivate / reactivate / references. */
export class CartonTypeNotFoundError extends Error {
  readonly reasonCode = 'CARTON_TYPE_NOT_FOUND';
  constructor(message?: string) {
    super(message ?? 'Carton type id not found.');
    this.name = 'CartonTypeNotFoundError';
  }
}

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

// BE status code constants — mirror SmartMatrix.Domain.PackAnalysis.Messages.CartonType_*_Response.StatusCodes.
const STATUS_FINGERPRINT_DUPLICATE = -2700;
const STATUS_ALREADY_IN_SCOPE = -2702;
const STATUS_NOT_FOUND_UPDATE = -2710;
const STATUS_NOT_FOUND_SOFTDEACT = -2720;
const STATUS_HAS_ACTIVE_REFERENCES = -2721;
const STATUS_NOT_FOUND_REACTIVATE = -2730;
const STATUS_NOT_FOUND_REFERENCES = -2740;

// ============================================================================
// Service
// ============================================================================

export const cartonTypeService = {
  /**
   * List carton types for a tenant. Pass `page` + `pageSize` (and optionally
   * `q` / `category` / `includeInactive`) to opt into the paged + filtered
   * admin list. Pass only `tenantId` for the legacy un-paged behaviour.
   */
  async list(params: CartonTypeListParams): Promise<CartonTypeListResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<CartonTypeListResponse>>(
        API_PACK_ANALYSIS_CARTON_TYPES_LIST,
        {
          params: {
            tenantId: params.tenantId,
            q: params.q || undefined,
            category: params.category && params.category.length > 0 ? params.category : undefined,
            includeInactive: params.includeInactive ?? undefined,
            page: params.page ?? undefined,
            pageSize: params.pageSize ?? undefined,
            buyerPartyId: params.buyerPartyId ?? undefined,
            supplierPartyId: params.supplierPartyId ?? undefined,
          },
        }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to list carton types');
      }
      return response.data.data ?? { items: [], total: null, page: null, pageSize: null, availableCategories: [] };
    } catch (error) {
      logger.error('Failed to list carton types', error);
      throw error;
    }
  },

  /**
   * edi-master-data-scoped-self-serve — upsert a per-pair display override
   * (description / category) for a carton type. Both fields blank clears it.
   * Throws on failure (incl. Not_Authorized -2751 when the caller lacks
   * carton-type.manage for the pair).
   */
  async selfServeOverride(
    tenantId: number,
    id: number,
    request: CartonTypeSelfServeOverrideRequest,
  ): Promise<void> {
    try {
      const response = await apiClient.put<ResultEnvelope<unknown>>(
        API_PACK_ANALYSIS_CARTON_TYPES_SELF_SERVE_OVERRIDE(id),
        request,
        { params: { tenantId } },
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to save carton override');
      }
      toast.success('Saved for this scope');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to save: ${msg}`);
      throw error;
    }
  },

  /**
   * edi-carton-type-scoped-physical-attributes — un-adopt a carton from a
   * (buyer, supplier) pair: soft-deletes ALL the pair's alias rows (the ASN code
   * alias AND any display/tare override), so the carton leaves that pair's
   * catalog. The shared canonical and other pairs are untouched. Platform-default
   * seeds (no alias) are never removable this way — the FE hides the action.
   */
  async removeFromScope(
    tenantId: number,
    id: number,
    buyerPartyId: number,
    supplierPartyId: number,
  ): Promise<void> {
    try {
      const response = await apiClient.delete<ResultEnvelope<unknown>>(
        API_PACK_ANALYSIS_CARTON_TYPES_REMOVE_SCOPE(id),
        { params: { tenantId, buyerPartyId, supplierPartyId } },
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to remove carton from scope');
      }
      toast.success('Removed from this scope');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to remove: ${msg}`);
      throw error;
    }
  },

  async create(tenantId: number, request: CartonTypeCreateRequest): Promise<{ id: number; fingerprint: string }> {
    try {
      const response = await apiClient.post<ResultEnvelope<{ id: number; fingerprint: string }>>(
        API_PACK_ANALYSIS_CARTON_TYPES_CREATE,
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        if (response.data.statusCode === STATUS_FINGERPRINT_DUPLICATE) {
          throw new CartonTypeFingerprintDuplicateError(response.data.messages?.[0]);
        }
        if (response.data.statusCode === STATUS_ALREADY_IN_SCOPE) {
          throw new CartonTypeAlreadyInScopeError(response.data.messages?.[0]);
        }
        throw new Error(response.data.messages?.join(', ') || 'Failed to create carton type');
      }
      toast.success(`Created carton type ${response.data.data?.fingerprint ?? ''}`);
      return response.data.data ?? { id: 0, fingerprint: request.fingerprint ?? '' };
    } catch (error) {
      if (error instanceof CartonTypeFingerprintDuplicateError) throw error;
      if (error instanceof CartonTypeAlreadyInScopeError) throw error;
      const msg = getErrorMessage(error);
      toast.error(`Failed to create carton type: ${msg}`);
      throw error;
    }
  },

  async update(tenantId: number, id: number, request: CartonTypeUpdateRequest): Promise<{ id: number }> {
    try {
      const response = await apiClient.put<ResultEnvelope<{ id: number }>>(
        API_PACK_ANALYSIS_CARTON_TYPES_UPDATE(id),
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        if (response.data.statusCode === STATUS_NOT_FOUND_UPDATE) {
          throw new CartonTypeNotFoundError(response.data.messages?.[0]);
        }
        throw new Error(response.data.messages?.join(', ') || 'Failed to update carton type');
      }
      toast.success('Carton type updated');
      return response.data.data ?? { id };
    } catch (error) {
      if (error instanceof CartonTypeNotFoundError) throw error;
      const msg = getErrorMessage(error);
      toast.error(`Failed to update carton type: ${msg}`);
      throw error;
    }
  },

  /**
   * Soft-deactivate. Returns the active reference count when blocked so the
   * caller can render the hard-block dialog without an extra round-trip.
   */
  async deactivate(tenantId: number, id: number): Promise<{ id: number }> {
    try {
      const response = await apiClient.delete<ResultEnvelope<{ id: number; activeReferenceCount?: number }>>(
        API_PACK_ANALYSIS_CARTON_TYPES_DELETE(id),
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        if (response.data.statusCode === STATUS_HAS_ACTIVE_REFERENCES) {
          const count = response.data.data?.activeReferenceCount ?? 0;
          throw new CartonTypeHasActiveReferencesError(count, response.data.messages?.[0]);
        }
        if (response.data.statusCode === STATUS_NOT_FOUND_SOFTDEACT) {
          throw new CartonTypeNotFoundError(response.data.messages?.[0]);
        }
        throw new Error(response.data.messages?.join(', ') || 'Failed to deactivate carton type');
      }
      toast.success('Carton type deactivated');
      return response.data.data ?? { id };
    } catch (error) {
      if (error instanceof CartonTypeHasActiveReferencesError) throw error;
      if (error instanceof CartonTypeNotFoundError) throw error;
      const msg = getErrorMessage(error);
      toast.error(`Failed to deactivate carton type: ${msg}`);
      throw error;
    }
  },

  async reactivate(tenantId: number, id: number): Promise<{ id: number }> {
    try {
      const response = await apiClient.put<ResultEnvelope<{ id: number }>>(
        API_PACK_ANALYSIS_CARTON_TYPES_REACTIVATE(id),
        null,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        if (response.data.statusCode === STATUS_NOT_FOUND_REACTIVATE) {
          throw new CartonTypeNotFoundError(response.data.messages?.[0]);
        }
        throw new Error(response.data.messages?.join(', ') || 'Failed to reactivate carton type');
      }
      toast.success('Carton type reactivated');
      return response.data.data ?? { id };
    } catch (error) {
      if (error instanceof CartonTypeNotFoundError) throw error;
      const msg = getErrorMessage(error);
      toast.error(`Failed to reactivate carton type: ${msg}`);
      throw error;
    }
  },

  /**
   * Pre-deactivate reference check + detail-drawer "Referenced by" data.
   * Returns 0 when there are no currently-effective references.
   */
  async getReferences(tenantId: number, id: number, limit = 20): Promise<CartonTypeReferenceCountResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<CartonTypeReferenceCountResponse>>(
        API_PACK_ANALYSIS_CARTON_TYPES_REFERENCES(id),
        { params: { tenantId, limit } }
      );
      if (!response.data.succeeded) {
        if (response.data.statusCode === STATUS_NOT_FOUND_REFERENCES) {
          throw new CartonTypeNotFoundError(response.data.messages?.[0]);
        }
        throw new Error(response.data.messages?.join(', ') || 'Failed to load carton type references');
      }
      return response.data.data ?? { cartonTypeId: id, activeApprovedListCount: 0, activeProfileIds: [] };
    } catch (error) {
      if (error instanceof CartonTypeNotFoundError) throw error;
      logger.error('Failed to load carton type references', error);
      throw error;
    }
  },
};
